import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, List } from '@pella/sash-framework';
import { Breadcrumbs, Layout, SEO, Image, CTA } from '../../components';
import { isValidArray } from '../../components/utilities/validations';

import DynamicComponent from '../../components/common/dynamic-component';
import Tabs from '../../components/template/replacement/tabs';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';
import RTAShowroomPanel from '../../components/template/homepage/rta-showroom-panel';

import styles from './index.module.scss';

const Replacement = (props) => {
	const pageData = props.data.contentstackReplacement;

	const renderIWCContent = (content, type) => (
		<>
			{isValidArray(content?.image) && (
				<Image
					data={content.image[0]}
					className={`w-full ${type === 'icons' ? styles.iwcImage_Icon : type === 'standard' ? 'mb-5' : ''}`}
				/>
			)}
			{content?.heading && <p className={`${type === 'icons' ? 'lg:text-xl' : type === 'card' ? 'my-0 p-5 lg:text-2xl' : 'lg:text-2xl'} my-2.5 font-semibold`}>{content.heading}</p>}
			{content?.description && <div dangerouslySetInnerHTML={{ __html: content.description }} />}
			{isValidArray(content?.cta) && type !== 'card' && (
				<List
					bullets='none'
					className={styles.iwcList}
				>
					{content.cta.map((cta, index) => (
						<li key={`iwc-cta-${index}`}>
							<CTA data={cta} />
						</li>
					))}
				</List>
			)}
		</>
	);

	return (
		<Layout
			id='template-Replacement'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.page_hero && <HeroSimple data={pageData.page_hero} />}

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (isValidArray(block.iwc_columns?.columns)) {
						return (
							<div
								key={index}
								className={block.iwc_columns.light_gray_background ? 'bg-neutral-50 py-10 lg:py-40' : 'my-10 lg:my-20'}
							>
								<Container>
									{block.iwc_columns.number && <p className={`${styles.secNumber} text-2xl sm:mb-6 lg:text-3xl`}>{block.iwc_columns.number}</p>}
									{block.iwc_columns.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.iwc_columns.heading}</h2>}
									{block.iwc_columns.description && (
										<div
											className={`${styles.secDescription} mb-5 sm:mb-10`}
											dangerouslySetInnerHTML={{ __html: block.iwc_columns.description }}
										/>
									)}

									<Row noGutters={block.iwc_columns.iwc_layout === 'icons'}>
										{block.iwc_columns.columns.map((col, index) => (
											<Column
												key={`column-${index}`}
												xs={col.column_mobile}
												sm={6}
												lg={col.column_desktop}
												className={`${col.justify === 'center' ? 'text-center' : ''} ${block.iwc_columns.iwc_layout === 'icons' ? 'p-0' : block.iwc_columns.iwc_layout === 'standard' ? 'lg:p-10' : ''}`}
											>
												<div className={`h-full ${col.light_gray_background ? 'bg-neutral-50 p-9 sm:py-11' : ''}`}>
													{block.iwc_columns.iwc_layout === 'card' ? (
														<CTA
															data={col.cta?.[0]}
															renderAs='link'
															className={`${styles.iwcCtaWrapper} block bg-white`}
														>
															{renderIWCContent(col, block.iwc_columns.iwc_layout)}
														</CTA>
													) : (
														renderIWCContent(col, block.iwc_columns.iwc_layout)
													)}
												</div>
											</Column>
										))}
									</Row>

									{isValidArray(block.iwc_columns.cta) && (
										<div className='mt-8 text-center lg:mt-12'>
											<CTA data={block.iwc_columns.cta[0]} />
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (isValidArray(block.before_and_after_cards?.dynamic)) {
						return (
							<div
								key={index}
								className={block.before_and_after_cards.light_gray_background ? 'bg-neutral-50 py-10 lg:py-20' : 'my-10 lg:my-20'}
							>
								<Container>
									<Row align='center'>
										<Column
											xs={12}
											lg={4}
											xl={5}
										>
											{block.before_and_after_cards.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.before_and_after_cards.heading}</h2>}
											{isValidArray(block.before_and_after_cards.cta) && <CTA data={block.before_and_after_cards.cta[0]} />}
										</Column>

										<Column
											xs={12}
											lg={8}
											xl={7}
										>
											<DynamicComponent data={block.before_and_after_cards} />
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.content_box != null) {
						return (
							<div
								key={index}
								className={`my-10 lg:my-20`}
							>
								<Container>
									<div className={styles.contentBox}>
										<Row>
											{isValidArray(block.content_box.image) && (
												<Column
													xs={12}
													lg={7}
												>
													<Image
														data={block.content_box.image[0]}
														className='h-full w-full'
													/>
												</Column>
											)}

											<Column
												xs={12}
												lg={5}
											>
												<div className='p-8 sm:p-12 lg:pl-9'>
													{block.content_box.heading && <h3 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.content_box.heading}</h3>}
													{isValidArray(block.content_box.numbered_content) && (
														<List bullets='none'>
															{block.content_box.numbered_content.map((item, index) => (
																<li
																	key={index}
																	className='mt-7'
																>
																	{item.subheading && (
																		<p className='mb-2.5 inline-flex items-center text-sm font-semibold lg:text-2xl'>
																			<span className={`${styles.contentBox_Subheading} mr-4 text-sm text-black`}>{index + 1}</span>
																			{item.subheading}
																		</p>
																	)}
																	{item.description && (
																		<div
																			dangerouslySetInnerHTML={{
																				__html: item.description,
																			}}
																		/>
																	)}
																</li>
															))}
														</List>
													)}
												</div>
											</Column>
										</Row>
									</div>
								</Container>
							</div>
						);
					}

					if (block.all_dynamic != null) {
						const classes = `relative ${block.all_dynamic.light_gray_background ? 'bg-neutral-50 py-10 lg:py-20' : block.all_dynamic.layout === 'container-bg-above' ? `${styles.secPartialGray_above} mb-10 lg:mb-20 pt-10 lg:pt-20` : block.all_dynamic.layout === 'container-bg-below' ? `${styles.secPartialGray_below} mt-10 lg:mt-20 pb-10 lg:pb-20 before:absolute before:left-0 before:bottom-0 before:h-3/6 before:w-full before:z-1 before:bg-neutral-500` : 'my-10 lg:my-20'}`;
						const hideContent = block.all_dynamic.dynamic?.[0]?.card_type === 'resource-2' ? true : false;

						return (
							<div
								key={index}
								className={classes}
							>
								<Container className='relative z-2'>
									{!hideContent && (
										<>
											{block.all_dynamic.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.all_dynamic.heading}</h2>}
											{block.all_dynamic.description && (
												<div
													className={`${styles.secDescription} mb-5 sm:mb-10`}
													dangerouslySetInnerHTML={{
														__html: block.all_dynamic.description,
													}}
												/>
											)}
											{block.all_dynamic.subheading && <h3 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.all_dynamic.subheading}</h3>}
										</>
									)}
								</Container>

								{isValidArray(block.all_dynamic.dynamic) && (
									<DynamicComponent
										data={block.all_dynamic}
										className='relative py-0'
										hideTitle
									/>
								)}
							</div>
						);
					}

					if (block.feature != null) {
						return (
							<div
								key={index}
								className={block.feature.light_gray_background ? 'bg-neutral-50 py-10 lg:py-20' : 'my-10 lg:my-20'}
							>
								<Container>
									<div className={`${styles.secFeatureGrid} grid ${block.feature.layout === 'diy' ? styles.secFeatureGrid_DIY : ''}`}>
										<div className={styles.secFeatureContent}>
											{block.feature.number && <p className={`${styles.secNumber} text-2xl sm:mb-6 lg:text-3xl`}>{block.feature.number}</p>}
											{block.feature.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.feature.heading}</h2>}
											{block.feature.description && (
												<div
													className={`${styles.secDescription} mb-5 sm:mb-10 ${block.feature.ul_style === 'checks' ? styles.featureCheck : ''}`}
													dangerouslySetInnerHTML={{
														__html: block.feature.description,
													}}
												/>
											)}
										</div>

										{isValidArray(block.feature.cta) && (
											<div className={`${styles.secFeatureCTA} text-center lg:text-left`}>
												<CTA data={block.feature.cta[0]} />
											</div>
										)}

										{isValidArray(block.feature.image) && (
											<Image
												data={block.feature.image[0]}
												className={`${styles.secFeatureImage} h-full w-full`}
											/>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.tabs?.tab_item)) {
						return (
							<div
								key={index}
								className='bg-neutral-50 py-10 lg:py-20'
							>
								<Container>
									{block.tabs.number && <p className={`${styles.secNumber} text-2xl sm:mb-6 lg:text-3xl`}>{block.tabs.number}</p>}
									{block.tabs.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.tabs.heading}</h2>}
									{block.tabs.description && (
										<div
											className={`${styles.secDescription} mb-5 text-lg sm:mb-10 lg:text-xl`}
											dangerouslySetInnerHTML={{
												__html: block.tabs.description,
											}}
										/>
									)}

									<Tabs data={block.tabs.tab_item} />
								</Container>
							</div>
						);
					}

					if (block.rta != null) {
						return (
							<div
								key={index}
								className={`${block.rta.layout === 'full-gray' ? 'bg-neutral-50 py-10 lg:py-20' : block.rta.layout === 'partially-gray' ? styles.secPartiallyGray : 'my-10 lg:my-20'}`}
							>
								<Container className='relative z-1'>
									<Row
										align='center'
										justify='between'
									>
										<Column
											xs={12}
											lg={7}
										>
											{block.rta.heading && <h2 className={`${styles.maxW30ch} mb-4 leading-none lg:mb-8`}>{block.rta.heading}</h2>}
											{block.rta.description && (
												<div
													className={styles.secDescription}
													dangerouslySetInnerHTML={{
														__html: block.rta.description,
													}}
												/>
											)}
										</Column>

										<Column
											xs={12}
											lg='auto'
										>
											{isValidArray(block.rta.form?.cta) && <CTA data={block.rta.form.cta[0]} />}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
								className={index === 0 ? styles.replacementCards : `${styles.replacementIconCards} relative`}
							/>
						);
					}

					if (isValidArray(block.dynamic?.dynamic)) {
						return (
							<div className='my-10 lg:my-20'>
								<DynamicComponent
									data={block.dynamic}
									key={index}
								/>
							</div>
						);
					}

					if (block.schedule_consult != null) {
						return (
							<RTAShowroomPanel
								data={block.schedule_consult}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

Replacement.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Replacement;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackReplacement.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackReplacement(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			page_hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
			}
			modular_blocks {
				iwc_columns {
					light_gray_background
					number
					heading
					description
					iwc_layout
					columns {
						light_gray_background
						column_desktop
						column_mobile
						justify
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						heading
						description
						cta {
							...CTA
						}
					}
					cta {
						...CTA
					}
				}
				before_and_after_cards {
					light_gray_background
					heading
					cta {
						...CTA
					}
					dynamic {
						...Dynamic
					}
				}
				content_box {
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					heading
					numbered_content {
						subheading
						description
					}
				}
				all_dynamic {
					light_gray_background
					heading
					description
					subheading
					layout
					dynamic {
						...Dynamic
					}
				}
				feature {
					light_gray_background
					layout
					number
					heading
					ul_style
					description
					cta {
						...CTA
					}
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
				tabs {
					number
					heading
					description
					tab_item {
						tab_heading
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						heading
						subheading
						features {
							file {
								url
								gatsbyImageData(quality: 90)
							}
							body
						}
						why_choose
						cta {
							...CTA
						}
					}
				}
				rta {
					layout
					heading
					description
					form {
						heading
						subheading
						cta {
							...CTA
						}
					}
				}
				cards {
					...CardSection
				}
				dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				schedule_consult {
					heading
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
